'use client';

import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { useEffect, useRef } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { usePathname } from 'next/navigation';

import { Button } from '@/components/ui/button/button.component';
import { setOpenLocationSelectionModal } from '@/store/modal';
import { userLocationStore, setUserLocation } from '@/store/user-location.store';
import { getUserLocationStorage } from '@/storage/location.storage';
import { ContentContainer } from '@/components/ui/content-container.component';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { slugify } from '@/utils/formatters/slugfy';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface LocationSectionProps {
  city?: string;
  state?: string;
}

export const LocationSection = ({ city, state }: LocationSectionProps) => {
  const userLocation = useStore(userLocationStore, (state) => state.userLocation);
  const { setParams, cidade: currentCity } = useSearchParams([...Object.values(PersistedUserNavigationSearchParams)]);
  const lastSetCityRef = useRef<string | null>(null);
  const analytics = getAnalyticsPlugin();
  const isHome = usePathname() === '/';

  useEffect(() => {
    if (city && state) {
      const cityValue = slugify(`${city}-${state}`);
      const place = {
        formattedAddress: '',
        latitude: 0,
        longitude: 0,
        types: [''],
        city,
        state,
        placeId: '',
      };
      setUserLocation(place);
      if (currentCity !== cityValue && lastSetCityRef.current !== cityValue) {
        lastSetCityRef.current = cityValue;
        setParams({
          [PersistedUserNavigationSearchParams.CIDADE]: cityValue,
        });
      }
    }
  }, [city, state, currentCity, setParams]);

  useEffect(() => {
    if (!city && !state && !userLocation && !isHome) {
      const saved = getUserLocationStorage();
      if (saved) {
        const cityValue = slugify(`${saved.city}-${saved.state}`);
        setUserLocation(saved);
        if (currentCity !== cityValue && lastSetCityRef.current !== cityValue) {
          lastSetCityRef.current = cityValue;
          setParams({
            [PersistedUserNavigationSearchParams.CIDADE]: cityValue,
          });
        }
      }
    }
  }, [city, state, userLocation, currentCity, setParams, isHome]);

  return (
    <>
      <section data-testid={DataTestIdEnum.CITY_LOCATION_BAR} className="bg-green-100 py-2 h-10">
        <ContentContainer>
          <Button
            variant="text"
            startIcon={faLocationDot}
            className="w-auto"
            onClick={() => {
              if (!userLocation) {
                analytics.track(AnalyticsEnum.CLICK_INFORM_LOCATION);
              }
              setOpenLocationSelectionModal(true);
            }}
          >
            {userLocation ? `${userLocation.city} - ${userLocation.state}` : 'Informar localização'}
          </Button>
        </ContentContainer>
      </section>
    </>
  );
};
